import type {
  ConciseAmendOperation,
  ConciseAnnulOperation,
  ConciseCreateOperation,
  ConciseHideOperation,
} from '@festina/configuration-general-types/configuration'
import { type BranchKey, LayerKey, STANDARD_BRANCH } from '@festina-life/api'
import { assertNotEmpty, notEmpty } from '@festina-life/core'
import { useTranslate } from '@festina-life/ui/hooks'
import { useMemo } from 'react'

import { useBaseBranchWorkspaces } from '../../../../hooks/useBaseBranchWorkspaces'
import type { ConciseChangesetWithBranchKey } from '../../../../uiModels/changesets/types'
import { isEqualWorkspace } from '../../../../workspace/helpers'
import { useWorkspaceManagerContext } from '../../../../workspace/workspaceManagerContext'
import { OperationApprovalStatus } from '../../../operationApproval/OperationApprovalStatus'
import { conciseWorkspaceToWorkspaceKey } from '../branchWorkspaces/helpers'
import { ConciseChangesetOperationLink } from './ConciseChangesetOperationLink'
import { isConciseAnnulOperation, isConciseEntityOperation } from './helpers'

type Props = {
  changeset: ConciseChangesetWithBranchKey
  branchKey: BranchKey
}

export const ConciseChangesetOperationsList = ({
  branchKey,
  changeset,
}: Props) => {
  const translate = useTranslate()
  const { currentWorkspaceKey } = useWorkspaceManagerContext()
  const baseBranchWorkspaces = useBaseBranchWorkspaces(branchKey)

  const isCurrentWorkspaceOnCurrentBranch = baseBranchWorkspaces.some(
    (workspace) =>
      isEqualWorkspace(
        conciseWorkspaceToWorkspaceKey({ workspace, server: branchKey.server }),
        currentWorkspaceKey,
      ),
  )

  const { supported, supportedAnnul } = useMemo(
    () =>
      (changeset.operations ?? []).filter(notEmpty).reduce<{
        supported: (
          | ConciseAmendOperation
          | ConciseCreateOperation
          | ConciseHideOperation
        )[]
        supportedAnnul: ConciseAnnulOperation[]
      }>(
        (accumulated, operation) => {
          if (isConciseEntityOperation(operation)) {
            accumulated.supported.push(operation)
          } else if (isConciseAnnulOperation(operation)) {
            accumulated.supportedAnnul.push(operation)
          }

          return accumulated
        },
        { supported: [], supportedAnnul: [] },
      ),
    [changeset.operations],
  )

  assertNotEmpty(changeset.block, translate('error.noBlocks'))
  const block = changeset.block
  const layer = new LayerKey({
    tenantId: changeset.tenantId,
    layerId: changeset.layer,
  })

  return (
    <>
      {[...supportedAnnul, ...supported].map((operation) => (
        <div key={operation.operationKey} className="flex items-center gap-x-2">
          {branchKey.name !== STANDARD_BRANCH && (
            <OperationApprovalStatus
              variant="compact"
              actions={[
                {
                  action: 'approve',
                  userId: operation.approverUserId,
                  actionTime: operation.approvalTime,
                },
                {
                  action: 'create',
                  userId: operation.creatorUserId,
                  actionTime: operation.creationTime,
                },
              ]}
            />
          )}
          <ConciseChangesetOperationLink
            operation={operation}
            commonLinkProps={{
              shouldShowLocationIndicators: false,
              layer,
              block,
              isLoading: false,
              branchKey,
              workspaceKey: isCurrentWorkspaceOnCurrentBranch
                ? currentWorkspaceKey
                : undefined,
              untilChangesetKey: changeset.changesetKey,
              operationKey: operation.operationKey,
              isDraft: false,
            }}
          />
        </div>
      ))}
    </>
  )
}
